import { MigrateFeatureRepoToUserSettingUseCase } from './UseCase/MigrateFeatureRepoToUserSetting';
import { arraysEqual, removeFromArray, lastElement } from '@standardnotes/utils';
import { ClientDisplayableError } from '@standardnotes/responses';
import { RoleName, ContentType } from '@standardnotes/domain-core';
import { ExperimentalFeatures, FindNativeFeature } from '@standardnotes/features';
import { FillItemContent, PayloadEmitSource, } from '@standardnotes/models';
import { AbstractService, ApiServiceEvent, API_MESSAGE_FAILED_OFFLINE_ACTIVATION, ApplicationStage, FeaturesEvent, FeatureStatus, StorageKey, AccountEvent, SubscriptionManagerEvent, ApplicationEvent, WebSocketsServiceEvent, } from '@standardnotes/services';
import { MigrateFeatureRepoToOfflineEntitlementsUseCase } from './UseCase/MigrateFeatureRepoToOfflineEntitlements';
import { GetFeatureStatusUseCase } from './UseCase/GetFeatureStatus';
export class FeaturesService extends AbstractService {
    constructor(storage, items, mutator, subscriptions, api, sockets, settings, user, sync, alerts, sessions, crypto, logger, isApplicationUsingThirdPartyHostUseCase, internalEventBus) {
        super(internalEventBus);
        this.storage = storage;
        this.items = items;
        this.mutator = mutator;
        this.subscriptions = subscriptions;
        this.api = api;
        this.settings = settings;
        this.user = user;
        this.sync = sync;
        this.alerts = alerts;
        this.sessions = sessions;
        this.crypto = crypto;
        this.logger = logger;
        this.isApplicationUsingThirdPartyHostUseCase = isApplicationUsingThirdPartyHostUseCase;
        this.internalEventBus = internalEventBus;
        this.onlineRoles = [];
        this.offlineRoles = [];
        this.enabledExperimentalFeatures = [];
        this.getFeatureStatusUseCase = new GetFeatureStatusUseCase(this.items);
        /** Fork (self-hosted server): every feature is available to every account, regardless of subscription */
        this.allFeaturesEntitled = true;
        this.PROD_OFFLINE_FEATURES_URL = 'https://apinotes.radmit.pl/v1/offline/features';
        this.eventDisposers.push(sockets.addEventObserver(async (eventName, data) => {
            if (eventName === WebSocketsServiceEvent.UserRoleMessageReceived) {
                const currentRoles = data.payload.currentRoles;
                void this.updateOnlineRolesWithNewValues(currentRoles);
            }
        }));
        this.eventDisposers.push(subscriptions.addEventObserver((event) => {
            if (event === SubscriptionManagerEvent.DidFetchSubscription) {
                void this.notifyEvent(FeaturesEvent.FeaturesAvailabilityChanged);
            }
        }));
        this.eventDisposers.push(this.items.addObserver(ContentType.TYPES.ExtensionRepo, async ({ changed, inserted, source }) => {
            const sources = [
                PayloadEmitSource.InitialObserverRegistrationPush,
                PayloadEmitSource.LocalInserted,
                PayloadEmitSource.LocalDatabaseLoaded,
                PayloadEmitSource.RemoteRetrieved,
                PayloadEmitSource.FileImport,
            ];
            if (sources.includes(source)) {
                const items = [...changed, ...inserted];
                if (this.sessions.isSignedIntoFirstPartyServer()) {
                    void this.migrateFeatureRepoToUserSetting(items);
                }
                else {
                    void this.migrateFeatureRepoToOfflineEntitlements(items);
                }
            }
        }));
        this.eventDisposers.push(this.user.addEventObserver((eventName) => {
            if (eventName === AccountEvent.SignedInOrRegistered) {
                const featureRepos = this.items.getItems(ContentType.TYPES.ExtensionRepo);
                const isThirdPartyHostUsedOrError = this.isApplicationUsingThirdPartyHostUseCase.execute();
                if (isThirdPartyHostUsedOrError.isFailed()) {
                    return;
                }
                const isThirdPartyHostUsed = isThirdPartyHostUsedOrError.getValue();
                if (!isThirdPartyHostUsed) {
                    void this.migrateFeatureRepoToUserSetting(featureRepos);
                }
            }
        }));
    }
    initializeFromDisk() {
        this.onlineRoles = this.storage.getValue(StorageKey.UserRoles, undefined, []);
        this.offlineRoles = this.storage.getValue(StorageKey.OfflineUserRoles, undefined, []);
        this.enabledExperimentalFeatures = this.storage.getValue(StorageKey.ExperimentalFeatures, undefined, []);
    }
    async handleEvent(event) {
        switch (event.type) {
            case ApiServiceEvent.MetaReceived: {
                if (!this.sync) {
                    this.logger.warn('Handling events interrupted. Sync service is not yet initialized.', event);
                    return;
                }
                const { userRoles } = event.payload;
                void this.updateOnlineRolesWithNewValues(userRoles.map((role) => role.name));
                break;
            }
            case ApplicationEvent.ApplicationStageChanged: {
                const stage = event.payload.stage;
                switch (stage) {
                    case ApplicationStage.StorageDecrypted_09: {
                        this.initializeFromDisk();
                        break;
                    }
                    case ApplicationStage.FullSyncCompleted_13: {
                        if (!this.hasFirstPartyOnlineSubscription()) {
                            const offlineRepo = this.getOfflineRepo();
                            if (offlineRepo) {
                                void this.downloadOfflineRoles(offlineRepo);
                            }
                        }
                        break;
                    }
                }
            }
        }
    }
    enableExperimentalFeature(identifier) {
        this.enabledExperimentalFeatures.push(identifier);
        void this.storage.setValue(StorageKey.ExperimentalFeatures, this.enabledExperimentalFeatures);
        void this.notifyEvent(FeaturesEvent.FeaturesAvailabilityChanged);
    }
    disableExperimentalFeature(identifier) {
        removeFromArray(this.enabledExperimentalFeatures, identifier);
        void this.storage.setValue(StorageKey.ExperimentalFeatures, this.enabledExperimentalFeatures);
        const component = this.items
            .getItems([ContentType.TYPES.Component, ContentType.TYPES.Theme])
            .find((component) => component.identifier === identifier);
        if (!component) {
            return;
        }
        void this.mutator.setItemToBeDeleted(component).then(() => {
            void this.sync.sync();
        });
        void this.notifyEvent(FeaturesEvent.FeaturesAvailabilityChanged);
    }
    toggleExperimentalFeature(identifier) {
        if (this.isExperimentalFeatureEnabled(identifier)) {
            this.disableExperimentalFeature(identifier);
        }
        else {
            this.enableExperimentalFeature(identifier);
        }
    }
    getExperimentalFeatures() {
        return ExperimentalFeatures;
    }
    isExperimentalFeature(featureId) {
        return this.getExperimentalFeatures().includes(featureId);
    }
    getEnabledExperimentalFeatures() {
        return this.enabledExperimentalFeatures;
    }
    isExperimentalFeatureEnabled(featureId) {
        return this.enabledExperimentalFeatures.includes(featureId);
    }
    async setOfflineFeaturesCode(code) {
        try {
            const result = this.parseOfflineEntitlementsCode(code);
            if (result instanceof ClientDisplayableError) {
                return result;
            }
            const offlineRepo = (await this.mutator.createItem(ContentType.TYPES.ExtensionRepo, FillItemContent({
                offlineFeaturesUrl: result.featuresUrl,
                offlineKey: result.extensionKey,
                migratedToOfflineEntitlements: true,
            }), true));
            void this.sync.sync();
            return this.downloadOfflineRoles(offlineRepo);
        }
        catch (err) {
            return new ClientDisplayableError(`${API_MESSAGE_FAILED_OFFLINE_ACTIVATION()}, ${JSON.stringify(err)}`);
        }
    }
    getOfflineRepo() {
        const repos = this.items.getItems(ContentType.TYPES.ExtensionRepo);
        return repos.filter((repo) => repo.migratedToOfflineEntitlements)[0];
    }
    hasOfflineRepo() {
        return this.getOfflineRepo() != undefined;
    }
    async deleteOfflineFeatureRepo() {
        const repo = this.getOfflineRepo();
        if (repo) {
            await this.mutator.setItemToBeDeleted(repo);
            void this.sync.sync();
        }
    }
    parseOfflineEntitlementsCode(code) {
        try {
            const activationCodeWithoutSpaces = code.replace(/\s/g, '');
            const decodedData = this.crypto.base64Decode(activationCodeWithoutSpaces);
            const { featuresUrl, extensionKey, subscriptionId } = JSON.parse(decodedData);
            return {
                featuresUrl,
                extensionKey,
                subscriptionId,
            };
        }
        catch {
            return new ClientDisplayableError(API_MESSAGE_FAILED_OFFLINE_ACTIVATION());
        }
    }
    async downloadOfflineRoles(repo) {
        const result = await this.api.downloadOfflineFeaturesFromRepo({
            repo,
        });
        if (result instanceof ClientDisplayableError) {
            return result;
        }
        this.setOfflineRoles(result.roles);
    }
    async migrateFeatureRepoToUserSetting(featureRepos = []) {
        const usecase = new MigrateFeatureRepoToUserSettingUseCase(this.mutator, this.settings);
        await usecase.execute(featureRepos);
    }
    async migrateFeatureRepoToOfflineEntitlements(featureRepos = []) {
        const usecase = new MigrateFeatureRepoToOfflineEntitlementsUseCase(this.mutator);
        const updatedRepos = await usecase.execute({ featureRepos, prodOfflineFeaturesUrl: this.PROD_OFFLINE_FEATURES_URL });
        if (updatedRepos.length > 0) {
            await this.downloadOfflineRoles(updatedRepos[0]);
        }
        for (const repo of featureRepos) {
            await this.downloadOfflineRoles(repo);
        }
    }
    hasPaidAnyPartyOnlineOrOfflineSubscription() {
        return this.onlineRolesIncludePaidSubscription() || this.hasOfflineRepo() || this.hasFirstPartyOnlineSubscription();
    }
    hasFirstPartyOnlineSubscription() {
        return this.sessions.isSignedIntoFirstPartyServer() && this.subscriptions.hasOnlineSubscription();
    }
    hasFirstPartyOfflineSubscription() {
        const offlineRepo = this.getOfflineRepo();
        if (!offlineRepo || !offlineRepo.content.offlineFeaturesUrl) {
            return false;
        }
        const hasFirstPartyOfflineSubscription = offlineRepo.content.offlineFeaturesUrl === this.PROD_OFFLINE_FEATURES_URL;
        return hasFirstPartyOfflineSubscription || new URL(offlineRepo.content.offlineFeaturesUrl).hostname === 'localhost';
    }
    async updateOnlineRolesWithNewValues(roles) {
        const previousRoles = this.onlineRoles;
        const userRolesChanged = roles.some((role) => !this.onlineRoles.includes(role)) || this.onlineRoles.some((role) => !roles.includes(role));
        if (!userRolesChanged) {
            return;
        }
        this.setOnlineRoles(roles);
        const isInitialLoadRolesChange = previousRoles.length === 0;
        if (!isInitialLoadRolesChange) {
            const changedRoles = roles.filter((role) => !previousRoles.includes(role));
            const changedRolesIncludePaidSubscription = this.rolesIncludePaidSubscription(changedRoles);
            if (changedRolesIncludePaidSubscription) {
                await this.notifyEvent(FeaturesEvent.DidPurchaseSubscription);
            }
        }
    }
    setOnlineRoles(roles) {
        const rolesChanged = !arraysEqual(this.onlineRoles, roles);
        this.onlineRoles = roles;
        if (rolesChanged) {
            void this.notifyEvent(FeaturesEvent.UserRolesChanged);
        }
        this.storage.setValue(StorageKey.UserRoles, this.onlineRoles);
    }
    setOfflineRoles(roles) {
        const rolesChanged = !arraysEqual(this.offlineRoles, roles);
        this.offlineRoles = roles;
        if (rolesChanged) {
            void this.notifyEvent(FeaturesEvent.UserRolesChanged);
        }
        this.storage.setValue(StorageKey.OfflineUserRoles, this.offlineRoles);
    }
    isThirdPartyFeature(identifier) {
        const isNativeFeature = !!FindNativeFeature(identifier);
        return !isNativeFeature;
    }
    rolesIncludePaidSubscription(roles) {
        const paidRoles = [RoleName.NAMES.PlusUser, RoleName.NAMES.ProUser];
        return roles.some((role) => paidRoles.includes(role));
    }
    onlineRolesIncludePaidSubscription() {
        return this.rolesIncludePaidSubscription(this.onlineRoles);
    }
    rolesBySorting(roles) {
        return Object.values(RoleName.NAMES).filter((role) => roles.includes(role));
    }
    hasRole(roleName) {
        return this.onlineRoles.includes(roleName.value) || this.offlineRoles.includes(roleName.value);
    }
    hasMinimumRole(role) {
        if (this.allFeaturesEntitled) {
            return true;
        }
        const sortedAllRoles = Object.values(RoleName.NAMES);
        const sortedUserRoles = this.rolesBySorting(this.hasFirstPartyOnlineSubscription() ? this.onlineRoles : this.offlineRoles);
        const highestUserRoleIndex = sortedAllRoles.indexOf(lastElement(sortedUserRoles));
        const indexOfRoleToCheck = sortedAllRoles.indexOf(role);
        return indexOfRoleToCheck <= highestUserRoleIndex;
    }
    getFeatureStatus(featureId, options = {}) {
        if (this.allFeaturesEntitled) {
            return FeatureStatus.Entitled;
        }
        return this.getFeatureStatusUseCase.execute({
            featureId,
            firstPartyRoles: this.hasFirstPartyOnlineSubscription()
                ? { online: this.onlineRoles }
                : this.hasFirstPartyOfflineSubscription()
                    ? { offline: this.offlineRoles }
                    : undefined,
            hasPaidAnyPartyOnlineOrOfflineSubscription: this.hasPaidAnyPartyOnlineOrOfflineSubscription(),
            firstPartyOnlineSubscription: this.hasFirstPartyOnlineSubscription()
                ? this.subscriptions.getOnlineSubscription()
                : undefined,
            inContextOfItem: options.inContextOfItem,
        });
    }
    deinit() {
        super.deinit();
        this.onlineRoles = undefined;
        this.offlineRoles = undefined;
        this.storage = undefined;
        this.items = undefined;
        this.mutator = undefined;
        this.api = undefined;
        this.subscriptions = undefined;
        this.settings = undefined;
        this.user = undefined;
        this.sync = undefined;
        this.alerts = undefined;
        this.sessions = undefined;
        this.crypto = undefined;
    }
}
